"use client";

import {
  DndContext,
  type DndContextProps,
  DragEndEvent,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  type PointerSensorOptions,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  type SortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import React, { createContext, useCallback, useContext } from "react";
import { RiDraggable } from "react-icons/ri";

import { cx } from "@/lib/tailwind-utils";

// ---------------------------------------------------------------------------
// Internal context for sharing useSortable state with SortableDragHandle
// ---------------------------------------------------------------------------

interface SortableItemContextValue {
  attributes: React.HTMLAttributes<HTMLElement>;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  listeners: Record<string, Function> | undefined;
  isDragging: boolean;
}

const SortableItemContext = createContext<SortableItemContextValue | null>(
  null,
);

// ---------------------------------------------------------------------------
// SortableList
// ---------------------------------------------------------------------------

interface SortableListProps {
  items: string[];
  onReorder: (ids: string[], movedId: string) => void;
  className?: string;
  modifiers?: Modifier[];
  strategy?: SortingStrategy;
  dragScope?: string;
  accessibility?: DndContextProps["accessibility"];
  children: React.ReactNode;
}

interface ScopedPointerSensorOptions extends PointerSensorOptions {
  dragScope?: string;
}

class ScopedPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: (
        { nativeEvent: event }: React.PointerEvent,
        options: PointerSensorOptions,
      ) => {
        const { dragScope, onActivation } =
          options as ScopedPointerSensorOptions;
        const target = event.target;
        if (
          !event.isPrimary ||
          event.button !== 0 ||
          (dragScope &&
            (!(target instanceof Element) ||
              !target.closest(`[data-drag-scope="${dragScope}"]`)))
        ) {
          return false;
        }
        onActivation?.({ event });
        return true;
      },
    },
  ];
}

function SortableList({
  items,
  onReorder,
  className,
  modifiers,
  strategy = verticalListSortingStrategy,
  dragScope,
  accessibility,
  children,
}: SortableListProps) {
  const sensors = useSensors(
    useSensor(ScopedPointerSensor, {
      activationConstraint: { distance: 8 },
      dragScope,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.indexOf(String(active.id));
      const newIndex = items.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...items];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      onReorder(reordered, String(active.id));
    },
    [items, onReorder],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      accessibility={accessibility}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={strategy}>
        <div className={className}>{children}</div>
      </SortableContext>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// SortableItem
// ---------------------------------------------------------------------------

interface SortableItemProps {
  id: string;
  className?: string;
  // "y" (default) locks translate to the y axis — matches the original
  // vertical-list behavior. "both" lets items translate on x and y, required
  // for grid sorting.
  axis?: "y" | "both";
  disableTransition?: boolean;
  // Element to render as the sortable node. "tr" lets table rows sort without
  // breaking table markup.
  as?: "div" | "tr";
  children:
    | React.ReactNode
    | ((props: { isDragging: boolean }) => React.ReactNode);
}

function SortableItem({
  id,
  className,
  axis = "y",
  disableTransition = false,
  as: Element = "div",
  children,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, transition: disableTransition ? null : undefined });

  // The wrapper's transform creates a new stacking context, so z-index must
  // sit on the wrapper itself for the dragged item to lift above siblings.
  const style: React.CSSProperties = {
    transform: transform
      ? axis === "both"
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : `translate3d(0, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  return (
    <SortableItemContext.Provider value={{ attributes, listeners, isDragging }}>
      <Element ref={setNodeRef} style={style} className={className}>
        {typeof children === "function" ? children({ isDragging }) : children}
      </Element>
    </SortableItemContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// SortableDragHandle
// ---------------------------------------------------------------------------

interface SortableDragHandleProps {
  className?: string;
  children?: React.ReactNode;
  dragScope?: string;
  ariaLabel?: string;
  disabled?: boolean;
  // Fires for a plain click only; dnd-kit swallows the click that ends a drag.
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

function SortableDragHandle({
  className,
  children,
  dragScope,
  ariaLabel,
  disabled,
  onClick,
}: SortableDragHandleProps) {
  const ctx = useContext(SortableItemContext);
  if (!ctx) {
    throw new Error("SortableDragHandle must be used within a SortableItem");
  }
  const { attributes, listeners, isDragging } = ctx;

  return (
    <button
      type="button"
      data-drag-scope={dragScope}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "flex h-8 w-6 cursor-grab items-center justify-center rounded-md text-foreground-muted transition-colors",
        "hover:bg-hover hover:text-foreground-secondary",
        "active:cursor-grabbing",
        isDragging && "cursor-grabbing text-primary",
        className,
      )}
      {...attributes}
      {...(listeners as React.HTMLAttributes<HTMLButtonElement>)}
    >
      {children ?? <DragGripIcon />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// DragGripIcon
// ---------------------------------------------------------------------------

function DragGripIcon() {
  return <RiDraggable className="h-4 w-4" />;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { SortableList, SortableItem, SortableDragHandle, DragGripIcon };
